import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import type { Client, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Drawer } from '../components/ui/Drawer'
import { GlowCard } from '../components/ui/GlowCard'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const TYPE_TABS = [
  { value: '', label: 'All' },
  { value: 'client', label: 'Clients' },
  { value: 'employee', label: 'Employees' },
  { value: 'contractor', label: 'Contractors' },
  { value: 'other', label: 'Other' },
]

const TYPE_BADGE: Record<string, 'accent' | 'ok' | 'info' | 'default'> = {
  client: 'accent',
  employee: 'info',
  contractor: 'ok',
  other: 'default',
}

export default function Clients() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(searchParams.get('action') === 'new')
  const [formData, setFormData] = useState({ company_name: '', contact_name: '', email: '', phone: '', client_type: 'client' as string })
  const [saving, setSaving] = useState(false)

  const loadClients = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' })
      if (search) params.set('search', search)
      if (typeFilter) params.set('client_type', typeFilter)
      const result = await api.get<PaginatedResult<Client>>(`/clients?${params}`)
      setClients(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled by api interceptor */ }
    finally { setLoading(false) }
  }, [page, search, typeFilter])

  useEffect(() => { loadClients() }, [loadClients])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const result = await api.post<{ slug?: string }>('/clients', formData)
      setDrawerOpen(false)
      setFormData({ company_name: '', contact_name: '', email: '', phone: '', client_type: 'client' })
      if (result?.slug) {
        navigate(`/clients/${result.slug}`)
      } else {
        loadClients()
      }
    } catch { /* error shown by api */ }
    finally { setSaving(false) }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Clients</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>Add Client</Button>
      </div>

      {/* Type filter tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {TYPE_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => { setTypeFilter(t.value); setPage(1) }}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: typeFilter === t.value ? 600 : 400,
              color: typeFilter === t.value ? 'var(--accent)' : 'var(--text-3)',
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: typeFilter === t.value ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
              transition: 'all var(--dur-f)',
              fontFamily: 'var(--font)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <input
          type="search"
          placeholder="Search clients..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="input max-w-sm"
          style={{ fontSize: 13 }}
        />
      </div>

      {loading ? (
        <TableSkeleton cols={5} />
      ) : clients.length === 0 ? (
        <EmptyState
          title="Your client list starts here"
          description="Add your first client to get started."
          action={<Button onClick={() => setDrawerOpen(true)}>Add Client</Button>}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block">
            <GlowCard style={{ padding: 0 }}>
              <table className="v3-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Type</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>City</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => {
                    const ct = (c as Client & { client_type?: string }).client_type || 'client'
                    return (
                      <tr key={c.id} onClick={() => navigate(`/clients/${c.slug}`)} style={{ cursor: 'pointer' }}>
                        <td>
                          <span style={{
                            color: 'var(--accent)', fontWeight: 500, fontSize: 13,
                          }}>
                            {c.company_name}
                          </span>
                        </td>
                        <td>
                          <Badge v={TYPE_BADGE[ct] || 'default'} sm>
                            {ct.toUpperCase()}
                          </Badge>
                        </td>
                        <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{c.contact_name ?? '-'}</td>
                        <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{c.email ?? '-'}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>{c.phone ?? '-'}</td>
                        <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{c.city ?? '-'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </GlowCard>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {clients.map((c) => {
              const ct = (c as Client & { client_type?: string }).client_type || 'client'
              return (
                <Link key={c.id} to={`/clients/${c.slug}`} className="mobile-card block">
                  <div className="flex items-center gap-2">
                    <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--accent)' }}>{c.company_name}</p>
                    <Badge v={TYPE_BADGE[ct] || 'default'} sm>{ct.toUpperCase()}</Badge>
                  </div>
                  {c.contact_name && <p style={{ fontSize: 12, color: 'var(--text-2)' }}>{c.contact_name}</p>}
                  <div className="mobile-card-row mt-1">
                    {c.phone && <span style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>{c.phone}</span>}
                    {c.city && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{c.city}, {c.state}</span>}
                  </div>
                </Link>
              )
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <span style={{ fontSize: 13, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); if (searchParams.get('action')) setSearchParams({}, { replace: true }) }} title="Add Client">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Company Name *</label>
            <input
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="input"
              required
            />
          </div>
          <div>
            <label className="label">Contact Name</label>
            <input
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
            />
          </div>
          <Select
            label="Type"
            value={formData.client_type}
            onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
            options={[
              { value: 'client', label: 'Client' },
              { value: 'employee', label: 'Employee' },
              { value: 'contractor', label: 'Contractor' },
              { value: 'other', label: 'Other' },
            ]}
          />
          <Button type="submit" loading={saving} disabled={!formData.company_name} style={{ width: '100%' }}>
            {saving ? 'Saving...' : 'Create Client'}
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
